import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  Container,
  Paper,
  Select,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
  Avatar,
  Collapse,
  CircularProgress,
  useTheme,
} from "@mui/material";
import {
  CheckCircle,
  HourglassEmpty,
  Autorenew,
  Cancel,
  Schedule,
  Download,
  Search,
  ChatBubbleOutline,
  Close,
  Storage,
} from "@mui/icons-material";
import { useState, useEffect, useContext } from "react";
import AuthContext from "../AuthContext";
import ConfirmDialog from '../components/common/ConfirmationModal';
import Loading from '../components/common/Loading'
import {
  getLogs,
  getCompanyLogs,
  getAllUsers,
  getCompanyDepartment,
  getCompanyUsers,
  getCompanyDevices,
  getAllDevices,
  getAllDepartments,
  cancelOperation,
  verifyOtp,
  diskChangedLogs,
  resendOtp,
  checkValid
} from "../apis/axios";
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from "docx";
import { saveAs } from "file-saver";
import {
  retryUsbBlocking,
  retryUsbUnblock,
  retryDeleteDrive,
  retryFormatDrive,
  retryDeleteFileFolder,
  retryEncryptFileFolder,
  retryDecryptFileFolder,
  retryDeleteFileFormat,
  retryEncryptFileFormat,
  retryDecryptFileFormat,
} from "../apis/RetryService";
import certificateUrl from "../assets/certificate.pdf";
import OTPModal from "./New folder/OtpModal";
import CertificateView from "../components/common/CertificateView";

const normalizeStatus = (status) => {
  if (!status) return "Unknown";
  const normalized = status
    .replace(/_/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  const validStatuses = ["Completed", "Started", "In Progress", "Failed", "Queued", "Cancelled", "Disk Changed", "Otp Failed"];
  return validStatuses.includes(normalized) ? normalized : "Requested";
};
normalizeStatus("otp_failed")
const getIconStylesForStatus = (status, theme = "light") => {
  const normalizedStatus = normalizeStatus(status);

  const colors = {
    light: {
      Completed: { iconColor: "#1E88E5", backgroundColor: "#E3F2FD" }, // blue
      Started: { iconColor: "#FB8C00", backgroundColor: "#FFF3E0" }, // amber
      "In Progress": { iconColor: "#43A047", backgroundColor: "#E8F5E9" }, // green
      Failed: { iconColor: "#E53935", backgroundColor: "#FDECEA" }, // red
      Queued: { iconColor: "#8E24AA", backgroundColor: "#F3E5F5" }, // purple
      Cancelled: { iconColor: "#F57C00", backgroundColor: "#FFF3E0" }, // orange
      "Disk Changed": { iconColor: "#F9A825", backgroundColor: "#FFF8E1" }, // yellow
      Default: { iconColor: "#546E7A", backgroundColor: "#ECEFF1" }, // grey
    },
    dark: {
      Completed: { iconColor: "#64B5F6", backgroundColor: "#0D47A1" },
      Started: { iconColor: "#FFB74D", backgroundColor: "#4E342E" },
      "In Progress": { iconColor: "#81C784", backgroundColor: "#1B5E20" },
      Failed: { iconColor: "#EF9A9A", backgroundColor: "#B71C1C" },
      Queued: { iconColor: "#BA68C8", backgroundColor: "#4A148C" },
      Cancelled: { iconColor: "#FFB74D", backgroundColor: "#4E342E" },
      "Disk Changed": { iconColor: "#FFF176", backgroundColor: "#F57F17" },
      Default: { iconColor: "#B0BEC5", backgroundColor: "#37474F" },
    },
  };

  const themeColors = colors[theme] || colors.light;
  return themeColors[normalizedStatus] || themeColors.Default;
};


const getIconComponent = (status) => {
  const normalizedStatus = normalizeStatus(status);
  switch (normalizedStatus) {
    case "Completed":
      return CheckCircle;
    case "Started":
      return HourglassEmpty;
    case "In Progress":
      return Autorenew;
    case "Failed":
      return Cancel;
    case "Queued":
      return Schedule;
    case "Cancelled":
      return Cancel;
    case "Disk Changed":
      return Storage;
    case "Otp Failed":
      return ChatBubbleOutline;
    default:
      return HourglassEmpty;
  }
};

const formatActivityType = (type) => {
  if (!type) return "Unknown Activity";
  return type
    .split(/[_/]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const formatTime = (timeStr) => {
  const date = new Date(timeStr);
  if (isNaN(date.getTime())) {
    return "Invalid Time";
  }
  return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
};

const extractDate = (timeStr) => {
  const date = new Date(timeStr);
  if (isNaN(date.getTime())) {
    return "Unknown Date";
  }
  return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const formatJsonPath = (json, activityType) => {
  try {
    const parsed = JSON.parse(json);
    if (activityType === "format_drive" && parsed.path) {
      const driveLetter = parsed.path.replace(/\\+/g, "").replace(/:/g, "").trim();
      return `Drive Letter: ${driveLetter}`;
    } else if (parsed.path) {
      const cleanPath = parsed.path.replace(/\\+/g, "\\").replace(/^\\+|\\+$/g, "");
      return `Path: ${cleanPath}`;
    }
    return json;
  } catch (e) {
    return json;
  }
};

const retryApiMapping = {
  usb_blocking: retryUsbBlocking,
  usb_unblock: retryUsbUnblock,
  delete_drive: retryDeleteDrive,
  format_drive: retryFormatDrive,
  "delete_file/folder": retryDeleteFileFolder,
  encryption: retryEncryptFileFolder,
  decryption: retryDecryptFileFolder,
  delete_file_format: retryDeleteFileFormat,
  encrypt_file_format: retryEncryptFileFormat,
  decrypt_file_format: retryDecryptFileFormat,
};

export default function ActivityLog() {
  const auth = useContext(AuthContext);
  const theme = useTheme();
  const [loading, setLoading] = useState(false)
  const [logs, setLogs] = useState([]);
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [openRequest, setOpenRequest] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    dateRange: "All Dates",
    type: "All Activities",
    dept: "All Departments",
    device: "All Devices",
    user: "All Users",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [isDropdownDataLoaded, setIsDropdownDataLoaded] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [openModal, setOpenModal] = useState(false);
  const [modalMode, setModalMode] = useState("encrypt");
  const [password, setPassword] = useState("");
  const [currentLog, setCurrentLog] = useState(null);
  const [retriedOperations, setRetriedOperations] = useState(new Set());
  const [startedOperation, setStartedOperation] = useState(new Set());
  const [openCertModal, setOpenCertModal] = useState(false);
  const [openOTPModal, setOpenOTPModal] = useState(false);
  const [otpLog, setOtpLog] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [update, setUpdate] = useState(false)

  const handleOpenOTPModal = (item) => {
    console.log(item)
    setOtpLog(item);
    setStartedOperation(prev => new Set(prev).add(item.operationId)); // Add this
    setOpenOTPModal(true);
  };
  const handleUpdate = () => {
    setUpdate(!update)
  }
  const handleVerifyOtp = async (otp) => {
    try {
      const res = await verifyOtp({ operation_id: otpLog.operationId, otp: otp });
      if (res.data.success) {
        setSnackbarMessage("OTP verified successfully. Operation is being processed.");
        setSnackbarSeverity("success");
        handleUpdate();
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage(res.data.error || "OTP verification failed. Please try again.");
        setSnackbarSeverity("error");
        handleUpdate()
        setSnackbarOpen(true);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || "An unexpected error occurred. Please try again.";
      setSnackbarMessage(errorMsg);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      handleUpdate();
    }
    finally {
      setOpenOTPModal(false);
      handleUpdate();
    }
  };

  useEffect(() => {
    const fetchDropdownData = async () => {
      if (!auth) return;

      try {
        if (auth.userType === "superadmin") {
          const [usersRes, devicesRes, departmentsRes] = await Promise.all([
            getAllUsers(),
            getAllDevices(),
            getAllDepartments(),
          ]);
          setUsers(usersRes.data || []);
          setDevices(devicesRes.data || []);
          setDepartments(departmentsRes.data || []);
        } else if (auth.admin) {
          const [usersRes, devicesRes, departmentsRes] = await Promise.all([
            getCompanyUsers({ company_id: auth.admin }),
            getCompanyDevices({ company_id: auth.admin }),
            getCompanyDepartment({company_id:auth.admin}),
          ]);
          setUsers(usersRes.data || []);
          const uniqueDevices = devicesRes.data
            ? Array.from(new Map(devicesRes.data.map((device) => [device.id, device])).values())
            : [];
          setDevices(uniqueDevices);
          setDepartments(departmentsRes.data || []);
        }
        setIsDropdownDataLoaded(true);
      } catch (error) {
        setIsDropdownDataLoaded(true);
      }
    };

    fetchDropdownData();
  }, [auth, update]);

  const fetchData = async () => {
    setLoading()
    setIsLoading(true);
    try {
      if (!auth || (auth.userType !== "superadmin" && !auth.admin)) {
        setLogs({ status: "error", message: "Authentication context is not ready" });
        setActivities([]);
        setFilteredActivities([]);
        setIsLoading(false);
        return;
      }

      const activityTypeMapping = {
        "Delete File/Folder": "delete_file/folder",
        "Delete File Format": "delete_file_format",
        "Delete OS": "delete_os",
        "Encryption": "encryption",
        "Decryption": "decryption",
        "USB Blocking": "usb_blocking",
        "USB Unblock": "usb_unblock",
        "Encrypt File Format": "encrypt_file_format",
        "Decrypt File Format": "decrypt_file_format",
        "Format Drive": "format_drive",
        "Disk Changed": "disk_change",
        "All Activities": "",
      };

      const params = {
        dateRange: filters.dateRange,
        activityType: activityTypeMapping[filters.type] || "",
        department: filters.dept !== "All Departments" ? parseInt(filters.dept) : "",
        device: filters.device !== "All Devices" ? parseInt(filters.device) : "",
        userId: filters.user !== "All Users" ? parseInt(filters.user) : "",
        company_id: auth.admin || "",
        page: page,
      };

      let response;
      if (auth.userType === "superadmin") {
        response = await getLogs(params);
        console.log(response)
      } else {
        response = await getCompanyLogs(params);
      }

      if (response.data && response.data.status === "error") {
        setLogs(response.data);
        setActivities([]);
        setFilteredActivities([]);
        setIsLoading(false);
        return;
      }

      let logsArray;
      if (response.data && Array.isArray(response.data.data)) {
        logsArray = response.data.data;
      } else if (Array.isArray(response.data)) {
        logsArray = response.data;
      } else {
        logsArray = [];
      }

      const operationStatuses = {};
      logsArray.forEach((log) => {
        if (log.operation_id) {
          const normalizedStatus = normalizeStatus(log.status);
          if (!operationStatuses[log.operation_id]) {
            operationStatuses[log.operation_id] = new Set();
          }
          operationStatuses[log.operation_id].add(normalizedStatus);
        }
      });
      setLogs(logsArray);
      setHasMore(logsArray.length > 0);

      const groupedByDate = logsArray.reduce((acc, log) => {
        const date = extractDate(
          log.time && log.time !== "0000-00-00 00:00:00" ? log.time : log.created_At
        );
        if (!acc[date]) {
          acc[date] = [];
        }
        const normalizedStatus = normalizeStatus(log.status);
        const statusOrder = [
          "Requested",
          "Otp Failed",
          "Queued",
          "Started",
          "In Progress",
          "Completed",
          "Cancelled",
          "Failed"
        ];
        const getLatestStatus = (statusSet) => {
          if (!statusSet || statusSet.size === 0) return null;
          return statusOrder.find(s => statusSet.has(s)) || null;
        };
        const historyStatuses = operationStatuses[log.operation_id] || new Set();
        const latestStatus = getLatestStatus(historyStatuses);

        const activityType = formatActivityType(log.type);
        const rawActivityType = log.type;
        const isDeleteActivity = ["delete_file/folder", "delete_file_format", "delete_os", "format_drive"].includes(rawActivityType);
        const showResponseText = isDeleteActivity && normalizedStatus === "Completed";
        const hasAnyProgress = operationStatuses[log.operation_id]?.has('Queued') ||
          operationStatuses[log.operation_id]?.has('Started') ||
          operationStatuses[log.operation_id]?.has('In Progress') ||
          operationStatuses[log.operation_id]?.has('Completed') ||
          operationStatuses[log.operation_id]?.has('Failed') ||
          operationStatuses[log.operation_id]?.has('Cancelled');
        acc[date].push({
          activity: activityType,
          status: normalizedStatus,
          message: log.text || "No description available",
          user: log.username || "Unknown User",
          department: log.department_name || "Unknown Department",
          time: formatTime(log.time && log.time !== "0000-00-00 00:00:00" ? log.time : log.updated_At || log.created_At),
          deviceName: log.device_name || "Unknown Device",
          deviceId: log.device_id,
          json: log.json || "No details",
          error: log.json && log.json.includes("error") ? log.json : null,
          operationId: log.operation_id,
          actions: (() => {
            const actions = [];
            if (normalizedStatus === "Completed" && log.assets && isDeleteActivity) {
              actions.push("Download Certificate");
            }
            if (
              normalizedStatus === "Queued" &&
              (
                !operationStatuses[log.operation_id] ||
                (
                  !operationStatuses[log.operation_id].has("Started") &&
                  !operationStatuses[log.operation_id].has("In Progress") &&
                  !operationStatuses[log.operation_id]?.has('Otp Failed') &&
                  !operationStatuses[log.operation_id].has("Completed") &&
                  !operationStatuses[log.operation_id].has("Failed")
                )
              )
            ) {
              actions.push("Cancel");
            }

            if (normalizedStatus === "Failed" && !retriedOperations.has(log.operation_id)) {
              actions.push("Retry");
            }
            // -------------------- SUBMIT OTP (if status Requested) --------------------
            if (normalizedStatus === "Requested" && latestStatus === "Requested") {
              const updatedAt = new Date(log.updated_At || log.created_At);
              const now = new Date();
              const diffHours = (now - updatedAt) / (1000 * 60 * 60);

              if (
                diffHours <= 24 &&
                !startedOperation.has(log.operation_id) &&
                !hasAnyProgress
              ) {
                actions.push("Submit OTP");
              }
            }

            // -------------------- RESEND OTP (if status Otp Failed) --------------------
            if (normalizedStatus === "Otp Failed" && latestStatus === "Otp Failed") {
              const updatedAt = new Date(log.updated_At || log.created_At);
              const now = new Date();
              const diffHours = (now - updatedAt) / (1000 * 60 * 60);

              if (diffHours <= 24) {
                actions.push("Resend OTP");
              }
            }
            // ---------------------------------------------------------

            return actions;
          })(),
          showResponseText: showResponseText,
          raw: log,
        });
        return acc;
      }, {});

      setActivities((prev) => {
        if (page === 1) {
          return Object.keys(groupedByDate).map((date) => ({
            date,
            items: groupedByDate[date],
          }));
        }

        const mergedByDate = {
          ...prev.reduce((acc, section) => {
            acc[section.date] = section.items;
            return acc;
          }, {}),
        };

        Object.keys(groupedByDate).forEach((date) => {
          if (mergedByDate[date]) {
            mergedByDate[date] = [...mergedByDate[date], ...groupedByDate[date]];
          } else {
            mergedByDate[date] = groupedByDate[date];
          }
        });

        const mergedActivities = Object.keys(mergedByDate)
          .map((date) => ({
            date,
            items: mergedByDate[date],
          }))
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        setLoading(false)
        return mergedActivities;
      });
    } catch (error) {
      setLogs({ status: "error", message: error.message || "Failed to fetch logs" });
      setActivities([]);
      setFilteredActivities([]);
      setHasMore(false);
    } finally {
      setLoading(false)
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    setActivities([]);
    setFilteredActivities([]);
    setHasMore(true);
  }, [filters, auth, update]);

  useEffect(() => {
    if (!auth || (auth.userType !== "superadmin" && !auth.admin) || !isDropdownDataLoaded) {
      return;
    }
    fetchData();
  }, [page, JSON.stringify(filters), auth, isDropdownDataLoaded, update]);

  useEffect(() => {
    if (!searchQuery) {
      setFilteredActivities(activities);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = activities
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item) =>
            item.user.toLowerCase().includes(query) ||
            item.activity.toLowerCase().includes(query) ||
            item.deviceName.toLowerCase().includes(query) ||
            item.department.toLowerCase().includes(query) ||
            item.status.toLowerCase().includes(query) ||
            (item.message && item.message.toLowerCase().includes(query))
        ),
      }))
      .filter((section) => section.items.length > 0);

    setFilteredActivities(filtered);
  }, [searchQuery, activities]);

  const dropdownData = [
    {
      label: "Date Range",
      key: "dateRange",
      value: "All Dates",
      options: [
        { label: "All Dates", value: "All Dates" },
        { label: "Today", value: "Today" },
        { label: "Last 7 Days", value: "Last 7 Days" },
        { label: "Last 30 Days", value: "Last 30 Days" },
        { label: "Last 90 Days", value: "Last 90 Days" },
      ],
    },
    {
      label: "Activity Type",
      key: "type",
      value: "All Activities",
      options: [
        { label: "All Activities", value: "All Activities" },
        { label: "Delete File/Folder", value: "Delete File/Folder" },
        { label: "Delete File Format", value: "Delete File Format" },
        { label: "Delete OS", value: "Delete OS" },
        { label: "Encryption", value: "Encryption" },
        { label: "Decryption", value: "Decryption" },
        { label: "USB Blocking", value: "USB Blocking" },
        { label: "USB Unblock", value: "USB Unblock" },
        { label: "Encrypt File Format", value: "Encrypt File Format" },
        { label: "Decrypt File Format", value: "Decrypt File Format" },
        { label: "Format Drive", value: "Format Drive" },
      ],
    },
    {
      label: "Department",
      key: "dept",
      value: "All Departments",
      options: [
        { label: "All Departments", value: "All Departments" },
        ...departments.map((dept) => ({
          label: dept.name,
          value: String(dept.id),
        })),
      ],
    },
    {
      label: "Devices",
      key: "device",
      value: "All Devices",
      options: [
        { label: "All Devices", value: "All Devices" },
        ...devices.map((device) => ({
          label: device.name,
          value: String(device.id),
        })),
      ],
    },
    {
      label: "Users",
      key: "user",
      value: "All Users",
      options: [
        { label: "All Users", value: "All Users" },
        ...users.map((user) => ({
          label: user.name,
          value: String(user.id),
        })),
      ],
    },
  ];

  const handleExportLog = () => {
    const exportDate = new Date().toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    const doc = new Document({
      sections: [
        {
          children: [
            new Paragraph({
              text: "Activity Log",
              heading: HeadingLevel.HEADING_1,
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: `Exported on: ${exportDate}`,
                  color: "595C60",
                  size: 20,
                }),
              ],
            }),
            new Paragraph({ text: "" }),
            ...(filteredActivities.length === 0
              ? [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "No activities found.",
                      color: "595C60",
                      size: 20,
                    }),
                  ],
                }),
              ]
              : filteredActivities.flatMap((section) =>
                section.items.flatMap((item, idx) => {
                  const isExpanded = expandedCards[`${section.date}-${idx}`];
                  const formattedJson = [
                    "delete_file/folder",
                    "delete_file_format",
                    "delete_os",
                    "decryption",
                    "decrypt_file_format",
                    "encryption",
                    "encrypt_file_format",
                    "format_drive",
                  ].includes(item.raw.type)
                    ? formatJsonPath(item.json, item.raw.type)
                    : item.json;

                  const paragraphs = [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `${item.activity} - ${item.status}`,
                          bold: true,
                          size: 24,
                        }),
                      ],
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `${item.deviceName} - ${formattedJson}`,
                          color: "595C60",
                          size: 20,
                        }),
                      ],
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `By: ${item.user}    Department: ${item.department}`,
                          color: "595C60",
                          size: 20,
                        }),
                      ],
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `Time: ${item.time}`,
                          color: "595C60",
                          size: 20,
                        }),
                      ],
                    }),
                  ];

                  if (isExpanded && item.showResponseText) {
                    paragraphs.push(
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: `Response Text: ${item.message}`,
                            color: "FF7A00",
                            size: 20,
                          }),
                        ],
                        spacing: { before: 100 },
                      })
                    );
                  }

                  paragraphs.push(new Paragraph({ text: "" }));
                  return paragraphs;
                })
              )),
          ],
        },
      ],
    });

    Packer.toBlob(doc).then((blob) => {
      saveAs(blob, `activity-log-${new Date().toISOString().split("T")[0]}.docx`);
    });
  };

  const handleLoadMore = () => {
    setPage((prevPage) => prevPage + 1);
  };

  const handleDownloadCertificate = (log) => {
    setSelectedLog(log);
    setOpenCertModal(true);
  };

  const handleCancel = async (log) => {
    if (!log.operationId) {
      setSnackbarMessage("Cannot cancel operation: Missing operation ID.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      const response = await cancelOperation({ operation_id: log.operationId });

      if (response.data.success === true) {
        setSnackbarMessage("Operation cancelled successfully.");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setPage(1);
        setActivities([]);
        setFilteredActivities([]);
        setHasMore(true);
        fetchData();
      } else {
        setSnackbarMessage("Failed to cancel operation: " + (response.data.message || "Unknown error"));
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage("Error cancelling operation: " + (error.message || "Unknown error"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const openModalHandler = (mode, log) => {
    setModalMode(mode);
    setCurrentLog(log);
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setPassword("");
    setCurrentLog(null);
  };

  const handleConfirmRetryWithPassword = async () => {
    if (!password) {
      setSnackbarMessage(`${modalMode === "encrypt" ? "Encryption" : "Decryption"} password is required.`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      const retryFunction = modalMode === "encrypt" ? retryEncryptFileFolder : retryDecryptFileFolder;
      const response = await retryFunction(currentLog.raw, auth.user, password);

      if (response.success) {
        setRetriedOperations((prev) => new Set(prev).add(currentLog.raw.operation_id));
        setSnackbarMessage("Operation started successfully.");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setPage(1);
        setActivities([]);
        setFilteredActivities([]);
        setHasMore(true);
        fetchData();
      } else {
        setSnackbarMessage("Failed to restart operation: " + (response.message || "Unknown error"));
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage("Error restarting operation: " + (error.message || "Unknown error"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      closeModal();
    }
  };

  const handleRetry = async (log) => {
    const { raw } = log;
    if (!raw.operation_id) {
      setSnackbarMessage("Cannot retry operation: Missing operation ID.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const activityType = raw.type;
    const retryFunction = retryApiMapping[activityType];

    if (!retryFunction) {
      setSnackbarMessage(`Retry not supported for activity type: ${formatActivityType(activityType)}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // if (activityType === "encryption" || activityType === "encrypt_file_format") {
    //   openModalHandler("encrypt", log);
    //   return;
    // } else if (activityType === "decryption" || activityType === "decrypt_file_format") {
    //   openModalHandler("decrypt", log);
    //   return;
    // }

    try {
      const response = await retryFunction(raw, auth.user);

      if (response.success) {
        setRetriedOperations((prev) => new Set(prev).add(raw.operation_id));
        setStartedOperation((prev) => new Set(prev).add(raw.operation_id));
        setSnackbarMessage("Operation started successfully.");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setPage(1);
        setActivities([]);
        setFilteredActivities([]);
        setHasMore(true);
        fetchData();
      } else {
        setSnackbarMessage("Failed to restart operation: " + (response.message || "Unknown error"));
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage("Error restarting operation: " + (error.message || "Unknown error"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleResendOtp = async (log) => {
    const { raw } = log;
    if (!raw.operation_id) {
      setSnackbarMessage("Cannot retry operation: Missing operation ID.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const activityType = raw.type;
    const retryFunction = retryApiMapping[activityType];
    const payload = { licence_id: log.raw.licence_id, operation: activityType }

    const res = await checkValid(payload)
    if (res.data.status === "expired") {
      setSnackbarOpen(true)
      setSnackbarMessage("This license has expired.");
      return;
    }

    if (res.data.status === "not_allowed") {
      setSnackbarOpen(true)
      setSnackbarMessage("Operation is not permitted for this license.");
      return;
    }

    if (res.data.status !== "allowed") {
      setSnackbarOpen(true)
      setSnackbarMessage("Invalid license response.");
      return;
    }

    // if (activityType === "encryption" || activityType === "encrypt_file_format") {
    //   openModalHandler("encrypt", log);
    //   return;
    // } else if (activityType === "decryption" || activityType === "decrypt_file_format") {
    //   openModalHandler("decrypt", log);
    //   return;
    // }

    try {
      const payload = { operation_id: log.raw.operation_id, licence_id: raw.licence_id }
      const response = await resendOtp(payload);
      if (response.status === 'success') {
        setRetriedOperations((prev) => new Set(prev).add(raw.operation_id));
        setStartedOperation((prev) => new Set(prev).add(raw.operation_id));
        setSnackbarMessage("OTP resent successfully. Please contact to admin");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setPage(1);
        setActivities([]);
        setFilteredActivities([]);
        setHasMore(true);
        handleUpdate()
      } else {
        console.log(response)
        setSnackbarMessage("Failed to resend OTP: " + (response.message || "Unknown error"));
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.log(error)
      setSnackbarMessage("Error resending OTP: " + (error.message || "Unknown error"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const toggleExpandCard = (date, idx) => {
    const key = `${date}-${idx}`;
    setExpandedCards((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const selectSx = {
    minWidth: 150,
    height: 36,
    fontFamily: "Nunito Sans, sans-serif",
    fontSize: 16,
    color: theme.palette.text.primary,
    border: "0.6px solid #202224",
    borderRadius: "6px",
    backgroundColor: theme.palette.background.default,
    "& .MuiSelect-select": {
      py: 0.5,
      pr: 4,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&:focus-within": {
      borderColor: theme.palette.primary.main,
    },
    "& .MuiSelect-icon": {
      color: "#202224",
      fontSize: 16,
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        "& .MuiMenuItem-root": {
          fontFamily: "Nunito Sans, sans-serif",
          fontSize: 16,
          color: theme.palette.text.secondary,
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "&.Mui-selected": {
            backgroundColor: theme.palette.primary.main,
            color: "#FFFFFF",
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          },
        },
      },
    },
  };

  if (loading) {
    return <Loading />
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Container maxWidth={false} sx={{ px: { xs: 2, md: 0 } }}>
        {/* Header */}
        <Grid
          container
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
          sx={{ mb: 4 }}
        >
          <Grid item>
            <Typography variant="h4" fontWeight={700} sx={{ fontFamily: "Nunito Sans, sans-serif" }}>
              Activity Log
            </Typography>
          </Grid>
          <Grid item>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                size="small"
                placeholder="Search Activities"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#595C60", fontSize: 16 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    height: 36,
                    borderRadius: "20px",
                    backgroundColor: theme.palette.bg.default,
                    border: "0.6px solid #595C60",
                  },
                  "& .MuiInputBase-input": {
                    py: 0,
                    fontFamily: "Nunito Sans, sans-serif",
                    fontSize: 14,
                    color: "#595C60",
                  },
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
              />
              <Button
                variant="contained"
                onClick={handleExportLog}
                startIcon={<Download sx={{ fontSize: 16 }} />}
              >
                Export Log
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {/* Filters Card */}
        <Card sx={{ p: 3, mt: 4 }}>
          <Grid container spacing={4}>
            {dropdownData.map(({ label, key, options }) => (
              <Grid item size={{ xs: 12, sm: 12, md: 2.4 }} key={key}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                  color="text.secondary"
                  sx={{ fontFamily: "Nunito Sans, sans-serif", fontSize: 16 }}
                >
                  {label}
                </Typography>
                <Select
                  value={filters[key]}
                  onChange={(e) => setFilters({ ...filters, [key]: e.target.value })}
                  sx={selectSx}
                  MenuProps={menuProps}
                >
                  {options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            ))}
          </Grid>
        </Card>

        {/* Activities */}
        <Stack spacing={3} sx={{ mt: 4 }}>
          {logs?.status === "error" ? (
            <Typography color="error" textAlign="center">
              {logs.message}
            </Typography>
          ) : filteredActivities.length > 0 ? (
            filteredActivities.map((section) => (
              <Box key={section.date}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  fontWeight={500}
                  mb={2}
                  sx={{ fontFamily: "Nunito Sans, sans-serif" }}
                >
                  {section.date}
                </Typography>
                <Stack spacing={2}>
                  {section.items.map((item, idx) => {
                    const IconComp = getIconComponent(item.status);
                    const { iconColor: color, backgroundColor: bgColor } = getIconStylesForStatus(item.status);
                    const isExpanded = expandedCards[`${section.date}-${idx}`];
                    const formattedJson = [
                      "delete_file/folder",
                      "delete_file_format",
                      "delete_os",
                      "decryption",
                      "decrypt_file_format",
                      "encryption",
                      "encrypt_file_format",
                      "format_drive",
                    ].includes(item.raw.type)
                      ? formatJsonPath(item.json, item.raw.type)
                      : item.json;
                    return (
                      <Card key={idx} sx={{ backgroundColor: theme.palette.background.paper }}>
                        <CardContent sx={{ p: 2 }}>
                          <Grid container alignItems="flex-start" spacing={2} sx={{
                            flexDirection: { xs: "row", sm: "row" }, // mobile stack
                          }}>
                            {/* Icon Avatar - Left */}
                            <Grid item size={{ xs: 2, sm: 1.5 }}>
                              <Avatar sx={{ width: 60, height: 60, bgcolor: bgColor }}>
                                <IconComp sx={{ fontSize: 30, color }} />
                              </Avatar>
                            </Grid>

                            {/* Main Content - Name/Status Left Side */}
                            <Grid item size={{ xs: 10, sm: 7.5 }}>
                              <Stack spacing={1}>
                                <Typography
                                  variant="h6"
                                  fontWeight={600}
                                  sx={{ fontFamily: "Nunito Sans, sans-serif" }}
                                >
                                  {item.activity} - {item.status}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  color="text.secondary"
                                  sx={{ fontFamily: "Nunito Sans, sans-serif" }}
                                >
                                  {item.deviceName} - {formattedJson}
                                </Typography>
                                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontFamily: "Nunito Sans, sans-serif" }}
                                  >
                                    By: {item.user}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontFamily: "Nunito Sans, sans-serif" }}
                                  >
                                    Department: {item.department}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: "wrap" }}>
                                  {item.showResponseText && (
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={() => toggleExpandCard(section.date, idx)}
                                      sx={{ fontFamily: "Nunito Sans, sans-serif", fontSize: 14, fontWeight: 600 }}
                                    >
                                      Response Text
                                    </Button>
                                  )}
                                  {item.showResponseText && (
                                    <Button
                                      size="small"
                                      variant="text"
                                      color="warning"
                                      onClick={() => handleDownloadCertificate(item)}
                                      sx={{ fontFamily: "Nunito Sans, sans-serif", fontSize: 14, fontWeight: 600 }}
                                    >
                                      Download Certificate
                                    </Button>
                                  )}
                                  {item.actions.map((action, actionIdx) => (
                                    <Button
                                      key={actionIdx}
                                      size="small"
                                      variant="text"
                                      onClick={() => {
                                        if (action === "Download Certificate") {
                                          handleDownloadCertificate(item);
                                        } else if (action === "Cancel") {
                                          handleCancel(item);
                                        } else if (action === "Retry") {
                                          handleRetry(item);
                                        } else if (action === "Resend OTP") {
                                          handleResendOtp(item);
                                        } else if (action === "Submit OTP") {
                                          handleOpenOTPModal(item);
                                        }
                                      }}
                                      sx={{ fontFamily: "Nunito Sans, sans-serif", fontSize: 14, fontWeight: 600 }}
                                    >
                                      {action}
                                    </Button>
                                  ))}
                                </Stack>
                              </Stack>
                            </Grid>

                            {/* Time - Right Side */}
                            <Grid
                              item
                              size={{ xs: 2, sm: 3 }}
                              sx={{
                                display: "flex",
                                justifyContent: { xs: "flex-start", sm: "flex-end" },
                                alignItems: "flex-start"
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.disabled"
                                sx={{
                                  whiteSpace: "nowrap",
                                  fontFamily: "Nunito Sans, sans-serif",
                                  fontWeight: 500
                                }}
                              >
                                {item.time}
                              </Typography>
                            </Grid>
                          </Grid>

                          {/* Response Text Collapse */}
                          <Collapse in={isExpanded} sx={{ mt: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: { xs: 2, sm: 11.5 } }}>
                              <ChatBubbleOutline sx={{ fontSize: 14, color: "warning.main" }} />
                              <Typography
                                variant="body2"
                                color="warning.main"
                                fontWeight={600}
                                sx={{ fontFamily: "Nunito Sans, sans-serif" }}
                              >
                                {item.message}
                              </Typography>
                            </Box>
                          </Collapse>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              </Box>
            ))
          ) : (
            <Typography color="text.secondary" textAlign="center">
              No activities found.
            </Typography>
          )}
        </Stack>

        {/* Load More */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Button
            variant="outlined"
            onClick={handleLoadMore}
            disabled={isLoading || !hasMore}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              fontFamily: "Nunito Sans, sans-serif",
              borderColor: "#595C60",
              color: "#595C60",
              backgroundColor: "#FCFDFD",
            }}
          >
            {hasMore ? "Load More Activities" : "No More Activities"}
          </Button>
        </Box>
      </Container>

      {/* Password Modal */}
      <Modal open={openModal} onClose={closeModal}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            p: { xs: 2, sm: 3 },
            borderRadius: '24px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: theme.shadows[6],
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ fontFamily: "Nunito Sans, sans-serif" }}
            >
              {modalMode === "encrypt" ? "Encryption Settings" : "Decryption Settings"}
            </Typography>
            <IconButton onClick={closeModal}>
              <Close sx={{ color: theme.palette.bg.paper }} />
            </IconButton>
          </Box>
          <Stack spacing={2}>
            <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "Nunito Sans, sans-serif" }}>
              {modalMode === "encrypt" ? "Encryption Password*" : "Decryption Password*"}
            </Typography>
            <TextField
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type="password"
              placeholder={
                modalMode === "encrypt" ? "Enter a strong password" : "Enter the decryption password"
              }
              fullWidth

            />
            {password && (
              <Typography variant="caption" color="error" sx={{ mt: 1, fontFamily: "Nunito Sans, sans-serif" }}>
                {modalMode === "encrypt"
                  ? "⚠️ Please store your encryption password securely. If forgotten, the encrypted data cannot be recovered or decrypted."
                  : "⚠️ Ensure the password matches the one used for encryption."}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              onClick={closeModal}
              sx={{ fontFamily: "Nunito Sans, sans-serif", textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmRetryWithPassword}
              sx={{ backgroundColor: theme.palette.bg.paper, fontFamily: "Nunito Sans, sans-serif", textTransform: "none" }}
            >
              Confirm
            </Button>
          </Stack>
        </Box>
      </Modal>

      <OTPModal
        open={openOTPModal}
        handleVerifyOtp={handleVerifyOtp}
        handleClose={() => setOpenOTPModal(false)}
      />
      <CertificateView
        open={openCertModal}
        handleClose={() => {
          setOpenCertModal(false);
          setSelectedLog(null);
        }}
        logDetails={selectedLog}
        certificateUrl={certificateUrl}
      />

      <ConfirmDialog
        open={openRequest}
        title="Request Sent"
        message="Request is sent to Admin, You Will Receive OTP Shortly"
        onConfirm={() => setOpenRequest(false)}
        onCancel={() => setOpenRequest(false)}
        confirmText="OK"
        cancelText="Cancel"
        confirmButtonColor="primary"
      />



      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          "& .MuiSnackbarContent-root": {
            bgcolor: snackbarSeverity === "success" ? theme.palette.bg.paper : "#E74C3C",
            color: "white",
            borderRadius: 1,
            fontFamily: "Nunito Sans, sans-serif",
            fontSize: 14,
          },
        }}
        message={snackbarMessage}
        action={
          <IconButton size="small" color="inherit" onClick={handleCloseSnackbar}>
            <Close fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
}